import { clientMessageSchema, type ClientMessage } from './client-messages.js';
import { serverMessageSchema, type ServerMessage } from './server-messages.js';

/**
 * 와이어 프레임 인코딩/디코딩.
 *
 * 디코딩은 절대 throw하지 않는다. 신뢰할 수 없는 입력을 다루므로
 * 실패는 값으로 돌려주고 호출부가 error 프레임으로 응답하게 한다.
 */

export type DecodeResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly reason: string };

function decode<T>(raw: string, schema: { safeParse: (v: unknown) => { success: boolean; data?: unknown; error?: unknown } }): DecodeResult<T> {
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return { ok: false, reason: 'JSON 파싱 실패' };
  }

  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    return { ok: false, reason: summarizeZodError(parsed.error) };
  }
  return { ok: true, value: parsed.data as T };
}

/** 클라이언트가 보낸 원문을 파싱한다. */
export function decodeClientMessage(raw: string): DecodeResult<ClientMessage> {
  return decode<ClientMessage>(raw, clientMessageSchema);
}

/** 서버가 보낸 원문을 파싱한다. */
export function decodeServerMessage(raw: string): DecodeResult<ServerMessage> {
  return decode<ServerMessage>(raw, serverMessageSchema);
}

export function encodeClientMessage(msg: ClientMessage): string {
  return JSON.stringify(msg);
}

export function encodeServerMessage(msg: ServerMessage): string {
  return JSON.stringify(msg);
}

/**
 * Zod 에러를 사람이 읽을 한 줄로 줄인다.
 *
 * 전체 issue 트리를 그대로 노출하면 내부 스키마 구조가 새어 나가고
 * 로그도 지저분해진다. 처음 몇 건만 경로와 함께 남긴다.
 */
function summarizeZodError(error: unknown): string {
  const issues = (error as { issues?: Array<{ path?: PropertyKey[]; message?: string }> } | undefined)?.issues;
  if (!issues?.length) return '스키마 검증 실패';

  return issues
    .slice(0, 3)
    .map((issue) => {
      const path = issue.path?.join('.') ?? '';
      return path ? `${path}: ${issue.message ?? 'invalid'}` : (issue.message ?? 'invalid');
    })
    .join(', ');
}
